
import { getAudioSettingsVisibility, getVideoSettingsVisibility } from '../settings/functions';

import {
    CLEAR_TOOLBOX_TIMEOUT,
    CLOSE_CARMODE_MODAL,
    HANGUP_MENU,
    OPEN_CARMODE_MODAL,
    SET_OVERFLOW_MENU_VISIBLE,
    SET_SHARE_MENU,
    SET_SHARE_MENU_VISIBLE,
    SET_TOOLBAR_HOVERED,
    SET_TOOLBOX_ALWAYS_VISIBLE,
    SET_TOOLBOX_ENABLED,
    SET_TOOLBOX_TIMEOUT,
    SET_TOOLBOX_TIMEOUT_MS,
    SET_TOOLBOX_VISIBLE,
    TOGGLE_OVERFLOW_MENU,
    TOGGLE_REACTIONS_BUTTON,
    TOGGLE_TOOLBOX_VISIBLE
} from './actionTypes';


/**
 * Centralized selector to determine if the toolbox should be visible based on current state.
 * This consolidates all the scattered visibility logic throughout the codebase.
 *
 * @param {Object} state - The redux state.
 * @returns {boolean} True if the toolbox should be visible.
 */
export function getToolboxVisibilityState(state: Object) {
    const hangupSettingsVisible = state['features/settings'].hangupSettingsVisible;
    const isAudioVideoSettingsOpen = getAudioSettingsVisibility(state) || getVideoSettingsVisibility(state);

    const {
        alwaysVisible,
        enabled,
        visible,
        hovered,
        visibility,
        timeoutID,
        overflowMenuVisible,
        shareMenuVisible,
        iframeVisible
    } = state['features/toolbox'];

    const { audioSettingsVisible, videoSettingsVisible } = state['features/settings'];
    const chatOpen = state['features/chat'].isOpen;
    const participantsPaneOpen = state['features/participants-pane'].isOpen;
    const dialogOpen = Boolean(state['features/base/dialog'].component);
    const calleeInfoVisible = state['features/invite'].calleeInfoVisible;
    const reactionsOpen = state['features/reactions'].visible;
    const etherpadEditing = state['features/etherpad'].editing;

    // Core toolbox state conditions
    const coreVisible = enabled && (
        alwaysVisible
        || visible
        || timeoutID
        || audioSettingsVisible
        || videoSettingsVisible
    );

    // Dialog and component conditions that force visibility
    const forceVisible = overflowMenuVisible
        || shareMenuVisible
        || chatOpen
        || participantsPaneOpen
        || dialogOpen
        || visible
        || visibility
        || hovered
        || calleeInfoVisible
        || reactionsOpen
        || hangupSettingsVisible
        || isAudioVideoSettingsOpen
        || etherpadEditing
        || iframeVisible;

    const result = coreVisible || forceVisible;

    return result;
}

/**
 * Signals that toolbox timeout should be cleared.
 *
 * @returns {{
 *     type: CLEAR_TOOLBOX_TIMEOUT
 * }}
 */
export function clearToolboxTimeout(): Object {
    return {
        type: CLEAR_TOOLBOX_TIMEOUT
    };
}

/**
 * Shows/hides the overflow menu.
 *
 * @param {boolean} visible - True to show it or false to hide it.
 * @returns {{
 *     type: SET_OVERFLOW_MENU_VISIBLE,
 *     visible: boolean
 * }}
 */
export function setOverflowMenuVisible(visible: boolean): Object {
    return {
        type: SET_OVERFLOW_MENU_VISIBLE,
        visible
    };
}

/**
 *
 */
export function toggleShareMenu(visible: boolean): Object {
    return {
        type: SET_SHARE_MENU,
        visible
    };
}

/**
 *
 */
export function toggleOverflowMenu(overflowMenuVisible: boolean): Object {
    return {
        type: TOGGLE_OVERFLOW_MENU,
        overflowMenuVisible
    };
}

/**
 * Signals that toolbar is hovered value should be changed.
 *
 * @param {boolean} hovered - Flag showing whether toolbar is hovered.
 * @returns {{
 *     type: SET_TOOLBAR_HOVERED,
 *     hovered: boolean
 * }}
 */
export function setToolbarHovered(hovered: boolean): Object {
    return {
        type: SET_TOOLBAR_HOVERED,
        hovered
    };
}

/**
 *
 *
 * @param {boolean} value - Is open or not
 * @returns {{
 *     type: SET_TOOLBAR_HOVERED,
 *     value: boolean
 * }}
 */
export function toggleHangupMenu(value: boolean): Object {
    return {
        type: HANGUP_MENU,
        value
    };
}

/**
 * Docks/undocks the Toolbox.
 *
 * @param {boolean} bool - True if dock, false otherwise.
 * @returns {Function}
 */
export function setShareMenuVisible(bool: boolean) {
    return {
        type: SET_SHARE_MENU_VISIBLE,
        visible: bool
    };

}


/**
 * Signals that always visible toolbars value should be changed.
 *
 * @param {boolean} alwaysVisible - Value to be set in redux store.
 * @returns {{
 *     type: SET_TOOLBOX_ALWAYS_VISIBLE,
 *     alwaysVisible: boolean
 * }}
 */
export function setToolboxAlwaysVisible(alwaysVisible: boolean): Object {
    return {
        type: SET_TOOLBOX_ALWAYS_VISIBLE,
        alwaysVisible
    };
}

/**
 * Enables/disables the toolbox.
 *
 * @param {boolean} enabled - True to enable the toolbox or false to disable it.
 * @returns {{
 *     type: SET_TOOLBOX_ENABLED,
 *     enabled: boolean
 * }}
 */
export function setToolboxEnabled(enabled: boolean): Object {
    return {
        type: SET_TOOLBOX_ENABLED,
        enabled
    };
}

/**
 * Dispatches an action which sets new timeout and clears the previous one.
 *
 * @param {Function} handler - Function to be invoked after the timeout.
 * @param {number} timeoutMS - Delay.
 * @returns {{
 *     type: SET_TOOLBOX_TIMEOUT,
 *     handler: Function,
 *     timeoutMS: number
 * }}
 */
export function setToolboxTimeout(handler: Function, timeoutMS: number)
      : Object {
    return {
        type: SET_TOOLBOX_TIMEOUT,
        handler,
        timeoutMS
    };
}

/**
 * Dispatches an action which sets new toolbox timeout value.
 *
 * @param {number} timeoutMS - Delay.
 * @returns {{
 *     type: SET_TOOLBOX_TIMEOUT_MS,
 *     timeoutMS: number
 * }}
 */
export function setToolboxTimeoutMS(timeoutMS: number): Object {
    return {
        type: SET_TOOLBOX_TIMEOUT_MS,
        timeoutMS
    };
}

/**
 * Shows/hides the toolbox.
 *
 * @param {boolean} visible - True to show the toolbox or false to hide it.
 * @returns {{
 *     type: SET_TOOLBOX_VISIBLE,
 *     visible: boolean
 * }}
 */
export function setToolboxVisible(visible: boolean): Object {
    return {
        type: SET_TOOLBOX_VISIBLE,
        visible
    };
}

/**
 * Action to toggle the toolbox visibility.
 *
 * @returns {{
 *     type: TOGGLE_TOOLBOX_VISIBLE
 * }}
 */
export function toggleToolboxVisible() {
    return {
        type: TOGGLE_TOOLBOX_VISIBLE
    };
}

/**
 * Action to toggle the toolbox visibility.
 *
 * @returns {{
*     type: TOGGLE_REACTIONS_BUTTON
* }}
*/
export function toggleReactionsButton(value: boolean) {
    return {
        type: TOGGLE_REACTIONS_BUTTON,
        value
    };
}


/** .........
 * Displays the CarMode panel.
 *
 *
 * @returns {{
*     type: OPEN_CARMODE_MODAL
* }}
*/
export function openCarModeModal() {
    return {
        type: OPEN_CARMODE_MODAL
    };
}

/** .........
* Displays the CarMode panel.
*
*
* @returns {{
*     type: CLOSE_CARMODE_MODAL
* }}
*/
export function closeCarModeModal() {
    return {
        type: CLOSE_CARMODE_MODAL
    };
}
